import { LitElement, html, css, nothing, type PropertyValues } from 'lit';
import { customElement, query, state } from 'lit/decorators.js';

interface SignedInUser {
  id: number;
  email: string;
  name: string;
  role: string;
}

@customElement('fp3-login')
export class Fp3Login extends LitElement {
  @state() private email = '';
  @state() private password = '';
  @state() private code = '';
  @state() private challengeId = '';
  @state() private phoneHint = '';
  @state() private error = '';
  @state() private busy = false;
  @state() private smsConfigured: boolean | null = null;
  @state() private askRemember = false;
  @state() private remember = false;
  @query('dialog.remember') private rememberDialog?: HTMLDialogElement;

  static styles = css`
    :host {
      display: grid;
      place-items: center;
      min-height: calc(100vh - 5.5rem);
      padding: 1.5rem 1rem 2.5rem;
      box-sizing: border-box;
    }
    form[hidden] { display: none; }
    form {
      width: min(420px, 100%);
      display: grid;
      gap: 0.85rem;
      padding: 1.25rem 1.2rem 1.15rem;
      border: 1px solid #314255;
      border-radius: 12px;
      background: #121a22;
      box-sizing: border-box;
    }
    h1 {
      margin: 0;
      font-size: 1.35rem;
      font-weight: 600;
      letter-spacing: -0.02em;
    }
    p { margin: 0; color: #8fa3b8; line-height: 1.45; font-size: 0.9rem; }
    label {
      display: grid;
      gap: 0.3rem;
      font-size: 0.68rem;
      font-weight: 600;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: #8fa3b8;
    }
    input {
      width: 100%;
      box-sizing: border-box;
      padding: 0.6rem 0.7rem;
      border: 1px solid #314255;
      border-radius: 8px;
      background: #0f1419;
      color: #e8edf2;
      font: 400 0.95rem/1.3 inherit;
      letter-spacing: normal;
      text-transform: none;
    }
    input:focus { outline: 2px solid #5b9fd4; border-color: #5b9fd4; }
    button {
      justify-self: start;
      padding: 0.55rem 0.9rem;
      border: 1px solid #5b9fd4;
      border-radius: 8px;
      background: #5b9fd4;
      color: #0f1419;
      font: 600 0.9rem/1 inherit;
      cursor: pointer;
    }
    button:disabled { opacity: 0.6; cursor: wait; }
    .error { color: #f0a8a8; }
    .back {
      background: transparent;
      color: #8fa3b8;
      border-color: #314255;
    }
    .actions { display: flex; gap: 0.5rem; flex-wrap: wrap; }
    dialog {
      width: min(420px, calc(100vw - 2rem));
      margin: auto;
      padding: 1.25rem 1.2rem 1.15rem;
      border: 1px solid #314255;
      border-radius: 12px;
      background: #121a22;
      color: #e8edf2;
      box-sizing: border-box;
      box-shadow: 0 18px 50px rgba(0, 0, 0, 0.55);
    }
    dialog::backdrop { background: rgba(6, 10, 16, 0.84); }
    dialog h2 { margin: 0 0 0.45rem; font-size: 1.15rem; font-weight: 600; }
  `;

  connectedCallback() {
    super.connectedCallback();
    this.loadStatus();
  }

  updated(changed: PropertyValues) {
    if (!changed.has('askRemember')) return;
    const dialog = this.rememberDialog;
    if (!dialog) return;
    if (this.askRemember && !dialog.open) dialog.showModal();
    if (!this.askRemember && dialog.open) dialog.close();
  }

  private async loadStatus() {
    try {
      const res = await fetch('/api/auth/status');
      if (res.ok) {
        const body = await res.json();
        this.smsConfigured = Boolean(body.sms_configured);
      }
    } catch {
      this.smsConfigured = false;
    }
  }

  private async submitPassword(event: Event) {
    event.preventDefault();
    this.error = '';
    this.busy = true;
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: this.email, password: this.password, remember: true }),
      });
      if (!res.ok) {
        this.error = await readError(res);
        return;
      }
      const body = await res.json();
      if (!body.challenge_id) {
        this.finish(body);
        return;
      }
      this.challengeId = body.challenge_id;
      this.phoneHint = body.phone_hint;
      this.code = '';
      this.remember = false;
      this.askRemember = true;
    } catch {
      this.error = 'Sign-in is not reachable.';
    } finally {
      this.busy = false;
    }
  }

  private chooseRemember(remember: boolean) {
    this.remember = remember;
    this.askRemember = false;
  }

  private async submitCode(event: Event) {
    event.preventDefault();
    this.error = '';
    this.busy = true;
    try {
      const res = await fetch('/api/auth/mfa', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ challenge_id: this.challengeId, code: this.code, remember: this.remember }),
      });
      if (!res.ok) {
        this.error = await readError(res);
        return;
      }
      this.finish(await res.json());
    } catch {
      this.error = 'Sign-in is not reachable.';
    } finally {
      this.busy = false;
    }
  }

  private finish(user: SignedInUser & { access_token?: string }) {
    delete user.access_token;
    this.dispatchEvent(new CustomEvent<SignedInUser>('signed-in', { detail: user, bubbles: true, composed: true }));
  }

  render() {
    const setup = this.smsConfigured === false
      ? html`<p>Sign-in texts use Twilio Verify. In the Twilio console, open Verify, create a service named Flight Blue LLC, and set <strong>TWILIO_VERIFY_SERVICE_SID</strong> to that service SID (it starts with VA). Also set <strong>TWILIO_ACCOUNT_SID</strong> and <strong>TWILIO_AUTH_TOKEN</strong>. A purchased number and A2P registration are not required for these sign-in codes.</p>`
      : nothing;
    if (!this.challengeId) {
      return html`
        <form @submit=${this.submitPassword}>
          <h1>Sign in</h1>
          <p>Use the email and password an admin gave you. A text code is required again every 10 days.</p>
          ${setup}
          ${this.smsConfigured === false
            ? html`<p>The first admin is created from <strong>BOOTSTRAP_ADMIN_EMAIL</strong>, <strong>BOOTSTRAP_ADMIN_PASSWORD</strong>, and <strong>BOOTSTRAP_ADMIN_PHONE</strong> in that same file, then restart the backend. Later people are added from the People tab.</p>`
            : nothing}
          <label>Email
            <input type="email" name="email" autocomplete="username" required .value=${this.email}
              @input=${(event: Event) => { this.email = (event.target as HTMLInputElement).value; }} />
          </label>
          <label>Password
            <input type="password" name="password" autocomplete="current-password" required .value=${this.password}
              @input=${(event: Event) => { this.password = (event.target as HTMLInputElement).value; }} />
          </label>
          ${this.error ? html`<p class="error">${this.error}</p>` : nothing}
          <button type="submit" ?disabled=${this.busy}>${this.busy ? 'Checking…' : 'Continue'}</button>
        </form>
      `;
    }
    return html`
      <form @submit=${this.submitCode} ?hidden=${this.askRemember}>
        <h1>Enter the text code</h1>
        <p>We sent a 6-digit code to the number ending in ${this.phoneHint}. It expires in 10 minutes.</p>
        <label>Code
          <input name="code" inputmode="numeric" autocomplete="one-time-code" maxlength="6" required .value=${this.code}
            @input=${(event: Event) => { this.code = (event.target as HTMLInputElement).value; }} />
        </label>
        ${this.error ? html`<p class="error">${this.error}</p>` : nothing}
        <div class="actions">
          <button type="submit" ?disabled=${this.busy}>${this.busy ? 'Checking…' : 'Sign in'}</button>
          <button type="button" class="back" @click=${() => { this.challengeId = ''; this.askRemember = false; this.error = ''; }}>Back</button>
        </div>
      </form>
      <dialog class="remember" @close=${() => { this.askRemember = false; }}>
        <h2>Remember me for 10 days?</h2>
        <p>A text code is required again. Remember this browser so the next sign-ins only need your password until then. Each sign-in still ends after 24 hours.</p>
        <div class="actions">
          <button type="button" @click=${() => this.chooseRemember(true)}>Remember me</button>
          <button type="button" class="back" @click=${() => this.chooseRemember(false)}>Not this time</button>
        </div>
      </dialog>
    `;
  }
}

async function readError(res: Response) {
  try {
    const body = await res.json();
    return typeof body.detail === 'string' ? body.detail : 'Could not sign in.';
  } catch {
    return 'Could not sign in.';
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'fp3-login': Fp3Login;
  }
}
